const SERVICE_STATUSES = ['pending', 'done', 'paid'];

export const normalizeString = (value) => String(value || '').trim();
export const normalizeLower = (value) => normalizeString(value).toLowerCase();

export const normalizeStatus = (value) => {
  const normalized = normalizeLower(value);
  return SERVICE_STATUSES.includes(normalized) ? normalized : 'pending';
};

export const toNumber = (value, fallback = 0) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/** Human-readable size + unit for invoice lines (e.g. "5x6 · Sq. Ft."). */
export const formatLineMeasurement = (row = {}) => {
  const expr = normalizeString(row.qtyExpr);
  const display = normalizeString(row.qtyDisplay);
  const unit = normalizeString(row.unit);
  const qm = normalizeString(row.quantityMeasurement);
  const unitLabel = unit || qm;
  const qty = parseFloat(row.qty);
  const sizeParts = [];

  if (expr) sizeParts.push(expr);
  else if (display) sizeParts.push(display);

  const nos = toNumber(row.nos, 1);
  const length = toNumber(row.length, 0);
  const height = toNumber(row.height, 0);
  if (!expr && !display && (nos !== 1 || length > 0 || height > 0)) {
    sizeParts.push(`${nos}×${length}×${height}`);
  }

  if (!sizeParts.length && Number.isFinite(qty) && qty > 0) {
    sizeParts.push(String(qty));
  }

  const sizeText = sizeParts.join(' · ');
  if (!sizeText) return unitLabel;

  if (!unitLabel) return sizeText;

  const lower = sizeText.toLowerCase();
  const unitLower = unitLabel.toLowerCase();
  if (lower.includes(unitLower)) return sizeText;

  return `${sizeText} · ${unitLabel}`;
};

export const normalizeServiceEntry = (service = {}) => ({
  id: normalizeString(service.id) || `svc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  serviceName: normalizeLower(service.serviceName || service.tag || service.name),
  vendorId: normalizeString(service.vendorId),
  vendorName: normalizeString(service.vendorName),
  internalCost: service.internalCost === '' ? '' : toNumber(service.internalCost, 0),
  customerCharge: service.customerCharge === '' ? '' : toNumber(service.customerCharge, 0),
  showOnInvoice: Boolean(service.showOnInvoice),
  status: normalizeStatus(service.status),
  createdAt: service.createdAt || new Date().toISOString(),
});

export const normalizeBillRow = (row = {}) => ({
  ...row,
  tags: Array.isArray(row.tags) ? row.tags.map(normalizeLower).filter(Boolean) : [],
  services: Array.isArray(row.services) ? row.services.map(normalizeServiceEntry).filter(s => s.serviceName) : [],
});

export const normalizeServiceJob = (job = {}) => ({
  docType: normalizeString(job.docType || 'invoice'),
  billId: normalizeString(job.billId),
  billNo: normalizeString(job.billNo),
  billDate: job.billDate || null,
  partyId: normalizeString(job.partyId),
  partyName: normalizeString(job.partyName),
  itemId: normalizeString(job.itemId),
  itemName: normalizeString(job.itemName),
  rowIndex: Number.isFinite(job.rowIndex) ? job.rowIndex : 0,
  serviceId: normalizeString(job.serviceId || job.id),
  serviceName: normalizeLower(job.serviceName),
  vendorId: normalizeString(job.vendorId),
  vendorName: normalizeString(job.vendorName),
  internalCost: toNumber(job.internalCost, 0),
  customerCharge: toNumber(job.customerCharge, 0),
  showOnInvoice: Boolean(job.showOnInvoice),
  status: normalizeStatus(job.status),
  createdAt: job.createdAt || null,
  qtyExpr: normalizeString(job.qtyExpr),
  qtyDisplay: normalizeString(job.qtyDisplay),
  qty: toNumber(job.qty, 0),
  unit: normalizeString(job.unit),
  quantityMeasurement: normalizeString(job.quantityMeasurement),
  nos: toNumber(job.nos, 1),
  length: toNumber(job.length, 0),
  height: toNumber(job.height, 0),
  measurementLabel: formatLineMeasurement(job),
});

export const getServiceStatuses = () => [...SERVICE_STATUSES];
